<script setup lang="ts">
/**
 * Inline display building block for the `render_kpi` tool: a row of KPI tiles
 * (`{ label, value, delta? }`). Display-only — the backend settles the tool
 * call automatically, so this block never emits a result.
 */
import { computed } from "vue";

interface KpiItem {
  label: string;
  value: string;
  delta?: number;
}

const props = defineProps<{
  input: Record<string, unknown>;
}>();

const items = computed<KpiItem[]>(() =>
  Array.isArray(props.input.items)
    ? (props.input.items as unknown[])
        .filter(
          (i): i is KpiItem =>
            !!i &&
            typeof i === "object" &&
            typeof (i as KpiItem).label === "string" &&
            typeof (i as KpiItem).value === "string",
        )
    : [],
);
</script>

<template>
  <div
    v-if="items.length > 0"
    class="not-prose grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(8rem,1fr))]"
    data-slot="chat-block-kpi"
  >
    <div
      v-for="(item, idx) in items"
      :key="`${item.label}-${idx}`"
      class="space-y-0.5 rounded-xl border border-border bg-card p-3"
    >
      <p class="truncate text-xs text-muted-foreground">{{ item.label }}</p>
      <p class="text-lg font-semibold tabular-nums text-foreground">{{ item.value }}</p>
      <p
        v-if="typeof item.delta === 'number'"
        :class="['text-xs tabular-nums', item.delta >= 0 ? 'text-emerald-600' : 'text-red-600']"
      >
        {{ item.delta >= 0 ? "+" : "" }}{{ item.delta }}
      </p>
    </div>
  </div>
</template>
